// Day-of FX conversion to CHF. The cash-flow page, portfolio and performance are
// all in CHF, so every non-CHF trade / dividend / withholding value is converted
// at the rate of its own day.
//
// This is now a thin view onto `pricebook.ts` rather than its own lookup: an
// exchange rate is a price, and once fiat is an asset there is nothing left to
// tell the two apart. What remains here is the CHF-shaped vocabulary the rest of
// the app speaks — `toChf`, `lookupRate` — so several dozen call sites keep
// reading the way they always did.
//
// Framework-free + tested so it runs identically on the server and the browser.

import { BASE_CURRENCY } from "@/finance/model/constants.js";
import {
  buildPriceBook,
  convert,
  priceAt,
  type PriceBook,
} from "@/finance/pricing/model/pricebook.js";

// Re-exported here because a page reaches the price book through this module,
// and a page that has not loaded rates yet still needs a book to render with.

export interface FxObservation {
  base: string; // e.g. "USD", "EUR"
  asOf: string; // YYYY-MM-DD
  rate: number; // 1 `base` == `rate` CHF
}

/** The one price structure, under the name the FX callers use for it. */
export type FxTable = PriceBook;

/** Group raw observations (quote assumed CHF) into the shared price book. */
export function buildFxTable(rows: readonly FxObservation[]): FxTable {
  return buildPriceBook(
    rows.map((r) => ({ base: r.base, quote: BASE_CURRENCY, asOf: r.asOf, price: r.rate })),
  );
}

/**
 * Value of one unit of `currency` in CHF on `date`: exact day if known, else the
 * nearest prior observation, else the earliest available. `null` when the
 * currency cannot be priced at all.
 */
export function lookupRate(table: FxTable, currency: string, date: string): number | null {
  return priceAt(table, currency, BASE_CURRENCY, date);
}

/** Convert a native amount to CHF at the day's rate; `null` if unconvertible. */
export function toChf(
  amount: number | null | undefined,
  currency: string,
  date: string,
  table: FxTable,
): number | null {
  return convert(amount, currency, BASE_CURRENCY, date, table);
}

/** Convert a native amount to USD at the day's cross-rate; `null` if
 *  unconvertible. Used to give a second, USD-denominated read on portfolio
 *  aggregates alongside the CHF ones — not for per-position figures, which
 *  stay in their own trading currency (see portfolio.ts). */
export function toUsd(
  amount: number | null | undefined,
  currency: string,
  date: string,
  table: FxTable,
): number | null {
  return convert(amount, currency, "USD", date, table);
}
